import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AuthLayout } from "../../components/AuthLayout";
import { OAuthButtons } from "./OAuthButtons";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { ErrorMessage } from "../../components/ErrorMessage";
import { Field } from "../../components/Field";

function friendly(message) {
  return String(message || "Something went wrong.").replace(/^\d{3}\s+/, "");
}

const MIN_PASSWORD = 8;

export function Signup() {
  const { signup, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [name, setName]               = useState("");
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [inviteCode, setInviteCode]   = useState("");
  const [inviteRequired, setInviteReq] = useState(false);
  const [error, setError]             = useState(null);
  const [busy, setBusy]               = useState(false);

  useEffect(() => {
    api.authConfig().then((c) => setInviteReq(c.invite_code_required)).catch(() => {});
  }, []);

  if (isAuthenticated) return <Navigate to="/" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setBusy(true);
    try {
      // Signup logs the new user in; a verification email goes out in parallel.
      await signup({ name, email, password, invite_code: inviteCode || undefined });
      navigate("/", { replace: true });
    } catch (err) {
      setError(friendly(err.message));
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Score rounds, track progress, own your archery."
      footer={
        <span>
          Already have an account? <Link to="/login">Sign in</Link>
        </span>
      }
    >
      <OAuthButtons />

      <form className="auth-form" onSubmit={onSubmit}>
        <ErrorMessage message={error} />

        <Field variant="auth" label="Name">
          <input
            type="text"
            className="input"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field variant="auth" label="Email">
          <input
            type="email"
            className="input"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Field variant="auth" label="Password">
          <input
            type="password"
            className="input"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="auth-hint">At least {MIN_PASSWORD} characters.</span>
        </Field>

        {inviteRequired && (
          <Field variant="auth" label="Invite code">
            <input
              type="text"
              className="input"
              autoComplete="off"
              required
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
            />
          </Field>
        )}

        <p className="auth-hint u-text-center">
          By creating an account you agree to our{" "}
          <Link to="/terms">Terms &amp; Conditions</Link>.
        </p>

        <button type="submit" className="btn primary auth-submit" disabled={busy}>
          {busy ? "Creating account…" : "Create account"}
        </button>
      </form>
    </AuthLayout>
  );
}
